import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { db } from '../../firebase/firebase';
import { getDoc, doc, collection, query, where, getDocs } from 'firebase/firestore';
import WIcon from '../shared/WIcon';

const TIER_COLORS = ['', '#10b981', '#00bf63', '#f59e0b'];
const TIER_LABELS = ['', 'All Staff', 'Manager', 'Executive'];

const formatPhone = (phone) => {
  if (!phone) return 'Not provided';
  return '•••••••' + phone.slice(-3);
};

const checkEligibility = (userData) => Boolean(userData?.companyId && userData?.tier && !userData?.disabled);

const initialsFor = (name, surname) => {
  const parts = [name, surname].filter(Boolean);
  if (parts.length === 0) return '—';
  return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase();
};

const Banner = ({ type = 'info', children }) => {
  const palette = {
    success: { bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.25)', color: '#0f8b5e' },
    warning: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.25)', color: '#92400e' },
    error:   { bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.20)',  color: '#dc2626' },
    info:    { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.20)', color: '#1d4ed8' },
  }[type] || {};
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 10,
      background: palette.bg, border: `1px solid ${palette.border}`,
      color: palette.color, fontSize: 13, fontWeight: 600,
    }}>{children}</div>
  );
};

const QRScanner = () => {
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [user, setUser] = useState(null);
  const [isEligible, setIsEligible] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const scannerInstanceRef = useRef(null);
  const scannerContainerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (scannerInstanceRef.current) {
        try { scannerInstanceRef.current.stop().catch(() => {}); } catch (_) { /* ignore */ }
        scannerInstanceRef.current = null;
      }
    };
  }, []);

  const startScanning = () => {
    setUser(null);
    setIsEligible(false);
    setMessage({ text: '', type: '' });
    setLoading(true);

    if (scannerInstanceRef.current) {
      try { scannerInstanceRef.current.stop().catch(() => {}); } catch (_) { /* ignore */ }
      scannerInstanceRef.current = null;
    }

    if (!scannerContainerRef.current) {
      setLoading(false);
      setMessage({ text: 'Scanner failed to initialise. Reload and try again.', type: 'error' });
      return;
    }

    setTimeout(() => {
      try {
        const qrboxSize = window.innerWidth < 400 ? Math.floor(window.innerWidth * 0.7) : 260;
        const html5QrCode = new Html5Qrcode('qr-scanner-container');
        scannerInstanceRef.current = html5QrCode;

        html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: qrboxSize, height: qrboxSize } },
          (decodedText) => {
            html5QrCode.stop().catch(() => {}).finally(() => {
              setScanning(false);
              setLoading(false);
              processQRData(decodedText);
            });
          },
          () => { /* per-frame errors are noisy — ignore */ },
        ).then(() => {
          setScanning(true);
          setLoading(false);
          setMessage({ text: 'Scanner active. Point the camera at an employee QR code.', type: 'info' });
        }).catch((err) => {
          console.error('Failed to start scanner:', err);
          setLoading(false);
          setMessage({ text: 'Could not access the camera. Check browser permissions.', type: 'error' });
        });
      } catch (err) {
        console.error('Error creating scanner:', err);
        setLoading(false);
        setMessage({ text: 'Failed to initialise scanner.', type: 'error' });
      }
    }, 200);
  };

  const stopScanning = () => {
    setLoading(true);
    if (!scannerInstanceRef.current) {
      setScanning(false);
      setLoading(false);
      return;
    }
    scannerInstanceRef.current.stop()
      .catch(() => {})
      .finally(() => {
        scannerInstanceRef.current = null;
        setScanning(false);
        setLoading(false);
      });
  };

  const recordScan = (userInfo) => {
    setScanHistory((prev) => [userInfo, ...prev].slice(0, 8));
  };

  const fetchUserById = async (userId) => {
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) throw new Error('User not found');
    const data = userSnap.data();

    let companyName = 'Unknown company';
    if (data.companyId) {
      const c = await getDoc(doc(db, 'companies', data.companyId));
      if (c.exists()) companyName = c.data().name || companyName;
    }

    const eligible = checkEligibility(data);
    const userInfo = { id: userSnap.id, ...data, companyName, timestamp: new Date().toISOString() };
    setUser(userInfo);
    setIsEligible(eligible);
    recordScan(userInfo);
    setMessage({
      text: eligible ? 'Verified. Employee is eligible for redemption.' : 'Verified, but the employee is not eligible.',
      type: eligible ? 'success' : 'warning',
    });
  };

  const fetchUserByPhone = async (phoneNumber) => {
    const normalized = phoneNumber.replace(/[^0-9+]/g, '');
    const snap = await getDocs(query(collection(db, 'users'), where('phone', '==', normalized)));
    if (snap.empty) throw new Error('User not found');
    return fetchUserById(snap.docs[0].id);
  };

  const processQRData = async (data) => {
    setLoading(true);
    setMessage({ text: 'Processing QR code…', type: 'info' });
    try {
      try {
        const json = JSON.parse(data);
        if (json && json.userId) { await fetchUserById(json.userId); return; }
      } catch (_) { /* not JSON */ }

      if (data.startsWith('benetica:user:')) {
        await fetchUserById(data.replace('benetica:user:', ''));
      } else if (/^[a-zA-Z0-9]{20,}$/.test(data)) {
        await fetchUserById(data);
      } else {
        await fetchUserByPhone(data);
      }
    } catch (err) {
      console.error('QR processing error:', err);
      setUser(null);
      setIsEligible(false);
      setMessage({ text: 'Invalid QR code or user not found.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const cornerStyle = (i) => ({
    position: 'absolute',
    width: 28,
    height: 28,
    borderColor: '#00bf63',
    borderStyle: 'solid',
    borderWidth: 0,
    ...(i === 0 ? { top: 12, left: 12, borderTopWidth: 3, borderLeftWidth: 3, borderRadius: '4px 0 0 0' } :
       i === 1 ? { top: 12, right: 12, borderTopWidth: 3, borderRightWidth: 3, borderRadius: '0 4px 0 0' } :
       i === 2 ? { bottom: 12, left: 12, borderBottomWidth: 3, borderLeftWidth: 3, borderRadius: '0 0 0 4px' } :
                 { bottom: 12, right: 12, borderBottomWidth: 3, borderRightWidth: 3, borderRadius: '0 0 4px 0' }),
  });

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Scanner panel */}
      <div className="loysta-card" style={{
        flex: '1 1 420px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 32,
        gap: 18,
      }}>
        <div style={{
          width: 320,
          height: 320,
          maxWidth: '100%',
          borderRadius: 16,
          background: '#0f172a',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {/* Camera mount */}
          <div
            id="qr-scanner-container"
            ref={scannerContainerRef}
            style={{
              position: 'absolute',
              inset: 0,
              opacity: scanning ? 1 : 0,
              transition: 'opacity 0.2s',
              pointerEvents: scanning ? 'auto' : 'none',
            }}
          />

          {/* Corner brackets */}
          {!scanning && [0, 1, 2, 3].map((i) => <div key={i} style={cornerStyle(i)} />)}

          {/* Scan line */}
          {scanning && (
            <div style={{
              position: 'absolute',
              left: 28, right: 28, height: 2,
              background: 'linear-gradient(90deg, transparent, #00bf63, transparent)',
              top: '45%',
              boxShadow: '0 0 8px rgba(0,191,99,0.6)',
              pointerEvents: 'none',
            }} />
          )}

          {/* Idle state graphic */}
          {!scanning && !loading && (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.18)', pointerEvents: 'none' }}>
              <WIcon name="qrCode" size={64} color="rgba(255,255,255,0.18)" />
            </div>
          )}

          {loading && (
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600 }}>
              Starting camera…
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
            {scanning ? 'Scanning…' : 'Ready to scan'}
          </div>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            Point the camera at the employee's QR code in the Loysta app.
          </div>
        </div>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '8px 18px', borderRadius: 999,
          background: scanning ? 'rgba(0,191,99,0.10)' : '#f1f5f9',
          border: `1px solid ${scanning ? 'rgba(0,191,99,0.20)' : '#e2e8f0'}`,
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: scanning ? '#00bf63' : '#94a3b8',
          }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: scanning ? '#00bf63' : '#64748b' }}>
            {scanning ? 'Scanner Active' : 'Scanner Idle'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          {!scanning ? (
            <button
              type="button"
              onClick={startScanning}
              disabled={loading}
              style={{
                padding: '11px 22px',
                background: '#00bf63', color: 'white',
                border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
                cursor: loading ? 'wait' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
            >
              <WIcon name="qrCode" size={16} color="white" />
              {loading ? 'Starting…' : 'Start scanning'}
            </button>
          ) : (
            <button
              type="button"
              onClick={stopScanning}
              disabled={loading}
              style={{
                padding: '11px 22px',
                background: 'white', color: '#dc2626',
                border: '1.5px solid rgba(239,68,68,0.3)', borderRadius: 10,
                fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >Stop scanning</button>
          )}
        </div>

        {message.text && (
          <div style={{ width: '100%', maxWidth: 360 }}>
            <Banner type={message.type}>{message.text}</Banner>
          </div>
        )}

        {user && (
          <div style={{
            width: '100%',
            maxWidth: 360,
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: `linear-gradient(135deg, ${TIER_COLORS[user.tier] || '#10b981'}, #c1ff72)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 800, color: '#0f172a', flexShrink: 0,
              }}>{initialsFor(user.name, user.surname)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                  {[user.name, user.surname].filter(Boolean).join(' ') || 'Unknown'}
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{user.companyName}</div>
              </div>
              <span className={`loysta-status ${isEligible ? 'active' : 'inactive'}`}>
                {isEligible ? 'Eligible' : 'Not eligible'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#94a3b8' }}>Tier</span>
              <span style={{ color: TIER_COLORS[user.tier] || '#0f172a', fontWeight: 700 }}>
                T{user.tier} · {TIER_LABELS[user.tier] || ''}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#94a3b8' }}>Phone</span>
              <span style={{ color: '#334155', fontWeight: 600 }}>{formatPhone(user.phone)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Recent scans sidebar */}
      <div className="loysta-card" style={{ width: 320, flexShrink: 0, padding: 0 }}>
        <div className="loysta-card-head">
          <span className="loysta-card-title">Recent scans</span>
        </div>
        {scanHistory.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No scans yet — when you scan a QR code, it'll appear here.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {scanHistory.map((s, i) => {
              const eligible = checkEligibility(s);
              return (
                <div
                  key={s.id + s.timestamp}
                  style={{
                    padding: '14px 18px',
                    borderBottom: i < scanHistory.length - 1 ? '1px solid #f8fafc' : 'none',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: eligible ? '#10b981' : '#f59e0b',
                    marginTop: 5, flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                      {[s.name, s.surname].filter(Boolean).join(' ') || 'Unknown'}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      {s.companyName} · <span style={{ color: TIER_COLORS[s.tier], fontWeight: 600 }}>T{s.tier}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                      {new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRScanner;
